import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Package, 
  Truck, 
  MapPin, 
  RefreshCw,
  Clock,
  DollarSign,
  Mail,
  User,
  ShoppingBag,
  AlertCircle,
  CheckCircle,
  XCircle,
  CreditCard,
  PackageCheck,
  Trash2,
  ChevronDown
} from 'lucide-react';
import { AdminTabs } from './AdminTabs';

const STATUS_CONFIG = {
  pending_payment: {
    label: 'Pending Payment',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: Clock
  },
  paid: {
    label: 'Paid',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: CreditCard
  },
  shipped: {
    label: 'Shipped',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: Truck
  },
  completed: {
    label: 'Completed',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: XCircle
  }
};

export const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = filter === 'shipping' 
        ? `${backendUrl}/api/orders/shipping`
        : `${backendUrl}/api/orders`;
      
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Failed to fetch orders');
      
      let data = await response.json();
      
      if (filter === 'pickup') {
        data = data.filter(order => order.deliveryMethod === 'pickup');
      }
      
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setOrders(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrder(orderId);
    setOpenDropdown(null);
    try {
      const response = await fetch(`${backendUrl}/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error('Failed to update order');
      
      const updatedOrder = await response.json();
      
      setOrders(prev => prev.map(order => 
        order.id === orderId ? updatedOrder : order
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingOrder(null);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    
    setUpdatingOrder(orderId);
    try {
      const response = await fetch(`${backendUrl}/api/orders/${orderId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete order');
      
      setOrders(prev => prev.filter(order => order.id !== orderId));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingOrder(null);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  useEffect(() => {
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getNextStatus = (currentStatus, requiresShipping) => {
    if (currentStatus === 'pending_payment') return 'paid';
    if (currentStatus === 'paid' && requiresShipping) return 'shipped';
    if (currentStatus === 'paid' && !requiresShipping) return 'completed';
    if (currentStatus === 'shipped') return 'completed';
    return null;
  };

  const shippingOrderCount = orders.filter(o => o.requiresShipping).length;
  const pendingCount = orders.filter(o => o.status === 'pending_payment').length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="touch-target rounded-full hover:bg-secondary transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <div>
              <h1 className="text-xl font-display text-foreground">Order Management</h1>
              <p className="text-xs text-muted-foreground">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </p>
            </div>
          </div>
          
          <Button
            onClick={fetchOrders}
            variant="outline"
            size="sm"
            disabled={loading}
            data-testid="refresh-orders-btn"
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      <AdminTabs />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div 
            className={cn(
              "bg-card border rounded-lg p-4 cursor-pointer transition-all",
              filter === 'all' ? "border-accent ring-1 ring-accent" : "border-border hover:border-slate-muted"
            )}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-foreground" />
              </div>
              <div>
                <p className="text-2xl font-medium text-foreground">{orders.length}</p>
                <p className="text-xs text-muted-foreground">Total Orders</p>
              </div>
            </div>
          </div>
          
          <div 
            className={cn(
              "bg-card border rounded-lg p-4 cursor-pointer transition-all",
              filter === 'shipping' ? "border-amber-500 ring-1 ring-amber-500" : "border-border hover:border-slate-muted"
            )}
            onClick={() => setFilter('shipping')}
            data-testid="filter-shipping"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-medium text-foreground">{shippingOrderCount}</p>
                <p className="text-xs text-muted-foreground">Need Shipping</p>
              </div>
            </div>
          </div>
          
          <div 
            className={cn(
              "bg-card border rounded-lg p-4 cursor-pointer transition-all",
              filter === 'pickup' ? "border-green-500 ring-1 ring-green-500" : "border-border hover:border-slate-muted"
            )}
            onClick={() => setFilter('pickup')}
            data-testid="filter-pickup"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-medium text-foreground">
                  {orders.filter(o => !o.requiresShipping).length}
                </p>
                <p className="text-xs text-muted-foreground">Local Pickup</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-medium text-foreground">{pendingCount}</p>
                <p className="text-xs text-muted-foreground">Pending Payment</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && orders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
            <p className="text-muted-foreground">Loading orders...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && orders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="w-16 h-16 text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-display text-foreground mb-2">No orders yet</h2>
            <p className="text-muted-foreground">
              {filter === 'shipping' 
                ? "No shipping orders to fulfill" 
                : filter === 'pickup'
                ? "No pickup orders pending"
                : "Orders will appear here when customers checkout"}
            </p>
          </div>
        )}

        {/* Orders List */}
        {orders.length > 0 && (
          <div className="space-y-4">
            {orders.map((order) => {
              const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;
              const nextStatus = getNextStatus(order.status, order.requiresShipping);
              
              return (
                <div 
                  key={order.id}
                  className={cn(
                    "bg-card border rounded-lg overflow-hidden transition-opacity",
                    order.requiresShipping && order.status !== 'completed' ? "border-amber-200" : "border-border",
                    updatingOrder === order.id && "opacity-50"
                  )}
                  data-testid={`order-card-${order.id}`}
                >
                  {/* Order Header */}
                  <div className={cn(
                    "px-4 py-3 flex items-center justify-between",
                    order.requiresShipping && order.status !== 'completed' ? "bg-amber-50" : "bg-secondary/30"
                  )}>
                    <div className="flex items-center gap-3">
                      {order.requiresShipping ? (
                        <Truck className="w-5 h-5 text-amber-600" />
                      ) : (
                        <MapPin className="w-5 h-5 text-green-600" />
                      )}
                      <div>
                        <p className="font-medium text-foreground text-sm">
                          Order #{order.id.slice(0, 8).toUpperCase()}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(order.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-medium text-foreground flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {order.orderTotal.toFixed(2)}
                        </p>
                        <span className={cn(
                          "text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1",
                          STATUS_CONFIG[order.status]?.color || "bg-gray-100 text-gray-700"
                        )}>
                          <StatusIcon className="w-3 h-3" />
                          {STATUS_CONFIG[order.status]?.label || order.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Order Body */}
                  <div className="p-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Customer Info */}
                      <div>
                        <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                          Customer
                        </h4>
                        <div className="space-y-1">
                          <p className="text-sm text-foreground flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" />
                            {order.shippingInfo.fullName}
                          </p>
                          <p className="text-sm text-foreground flex items-center gap-2">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            {order.shippingInfo.email}
                          </p>
                          {order.requiresShipping && order.shippingInfo.address && (
                            <p className="text-sm text-foreground flex items-start gap-2 mt-2">
                              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                              <span>
                                {order.shippingInfo.address}<br />
                                {order.shippingInfo.city}, {order.shippingInfo.state} {order.shippingInfo.zipCode}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Items */}
                      <div>
                        <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                          Items ({order.items.length})
                        </h4>
                        <div className="space-y-2">
                          {order.items.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              {item.image && (
                                <div className="w-10 h-10 bg-muted rounded overflow-hidden flex-shrink-0">
                                  <img 
                                    src={item.image} 
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-foreground truncate">{item.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  Qty: {item.quantity} × ${item.price?.toFixed(2) || 'Inquire'}
                                </p>
                                {item.dedication && (
                                  <p className="text-xs text-amber-700 italic mt-1" data-testid={`order-item-dedication-${idx}`}>
                                    ✍︎ Inscribe: “{item.dedication}”
                                  </p>
                                )}
                                {item.note && (
                                  <p className="text-xs text-blue-700 mt-1 whitespace-pre-line" data-testid={`order-item-note-${idx}`}>
                                    ▸ {item.note}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                          {order.items.length > 3 && (
                            <p className="text-xs text-muted-foreground">
                              +{order.items.length - 3} more items
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Order Summary & Actions */}
                    <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-4">
                      <div className="flex gap-4 text-sm">
                        <span className="text-muted-foreground">
                          Subtotal: <span className="text-foreground">${order.subtotal.toFixed(2)}</span>
                        </span>
                        <span className="text-muted-foreground">
                          Shipping: <span className="text-foreground">
                            {order.shippingCost === 0 ? 'Free' : `$${order.shippingCost.toFixed(2)}`}
                          </span>
                        </span>
                        <span className="text-muted-foreground">
                          Tax: <span className="text-foreground">${order.taxAmount.toFixed(2)}</span>
                        </span>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        {/* Quick Action Button */}
                        {nextStatus && order.status !== 'cancelled' && (
                          <Button
                            size="sm"
                            onClick={() => updateOrderStatus(order.id, nextStatus)}
                            disabled={updatingOrder === order.id}
                            className={cn(
                              "text-xs",
                              nextStatus === 'paid' && "bg-blue-600 hover:bg-blue-700",
                              nextStatus === 'shipped' && "bg-purple-600 hover:bg-purple-700",
                              nextStatus === 'completed' && "bg-green-600 hover:bg-green-700"
                            )}
                            data-testid={`order-${order.id}-quick-action`}
                          >
                            {nextStatus === 'paid' && <CreditCard className="w-3 h-3 mr-1" />}
                            {nextStatus === 'shipped' && <Truck className="w-3 h-3 mr-1" />}
                            {nextStatus === 'completed' && <PackageCheck className="w-3 h-3 mr-1" />}
                            Mark as {STATUS_CONFIG[nextStatus]?.label}
                          </Button>
                        )}
                        
                        {/* More Actions Dropdown */}
                        <div className="relative">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === order.id ? null : order.id);
                            }}
                            disabled={updatingOrder === order.id}
                            data-testid={`order-${order.id}-more-actions`}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          
                          {openDropdown === order.id && (
                            <div 
                              className="absolute right-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-10"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="py-1">
                                {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                                  <button
                                    key={status}
                                    onClick={() => updateOrderStatus(order.id, status)}
                                    disabled={order.status === status}
                                    className={cn(
                                      "w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-secondary transition-colors",
                                      order.status === status && "opacity-50 cursor-not-allowed"
                                    )}
                                  >
                                    <config.icon className="w-4 h-4" />
                                    {config.label}
                                    {order.status === status && (
                                      <CheckCircle className="w-3 h-3 ml-auto text-green-500" />
                                    )}
                                  </button>
                                ))}
                                <div className="border-t border-border my-1" />
                                <button
                                  onClick={() => deleteOrder(order.id)}
                                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete Order
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrdersPage;
